import { useState, useEffect, useCallback, useRef } from "react"
import { useParams, useNavigate, useSearchParams } from "react-router"
import AppLayout from "../../components/layout/AppLayout"
import { colors } from "../../lib/colors"
import StatCard from "../../components/analytics/StatCard"
import LineChart from "../../components/analytics/LineChart"
import BarChart from "../../components/analytics/BarChart"
import InstagramLinkButton from "../../components/analytics/InstagramLinkButton"
import DateRangePicker, { type DateRange } from "../../components/analytics/DateRangePicker"
import { api } from "../../lib/api"
import { useSelectedAccount } from "../../contexts/AccountContext"
import { downloadCsv, downloadChartAsPng, chartDataToCsvRows, todayStr } from "../../lib/csv"
import type { CompetitorDetail } from "../../types/analytics"
import "../../styles/analytics.scss"

const toDateStr = (d: Date) => d.toISOString().split("T")[0]

const CompetitorDetailPage = () => {
  const { competitorId } = useParams<{ competitorId: string }>()
  const [searchParams] = useSearchParams()
  const { selectedAccountId, setSingleAccountOnly } = useSelectedAccount()
  const navigate = useNavigate()
  const followerTrendRef = useRef<HTMLDivElement>(null)
  const hourChartRef     = useRef<HTMLDivElement>(null)
  const dayChartRef      = useRef<HTMLDivElement>(null)
  const accountId = parseInt(searchParams.get("account_id") ?? "") || selectedAccountId

  useEffect(() => {
    setSingleAccountOnly(true)
  }, [setSingleAccountOnly])

  const [detail, setDetail]   = useState<CompetitorDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [range, setRange]     = useState<DateRange>(() => {
    const end = new Date(); const start = new Date()
    start.setDate(start.getDate() - 29)
    return { startDate: toDateStr(start), endDate: toDateStr(end) }
  })

  const fetchData = useCallback(async () => {
    if (!accountId || !competitorId) { setLoading(false); return }
    setLoading(true)
    try {
      const params = new URLSearchParams({
        instagram_account_id: String(accountId),
        start_date: range.startDate,
        end_date:   range.endDate,
      })
      const res = await api.get<CompetitorDetail>(`/analytics/competitors/${competitorId}?${params}`)
      setDetail(res)
    } catch {
    } finally {
      setLoading(false)
    }
  }, [accountId, competitorId, range])

  useEffect(() => { fetchData() }, [fetchData])

  const followerTrendData = detail?.follower_trend && detail.follower_trend.labels.length > 0 ? {
    labels: detail.follower_trend.labels,
    datasets: [
      {
        label:       `@${detail.profile.username}`,
        data:        detail.follower_trend.competitor,
        borderColor: colors.primary,
        fill:        false,
      },
      {
        label:       "自アカウント",
        data:        detail.follower_trend.self,
        borderColor: colors.gray400,
        fill:        false,
      },
    ],
  } : null

  const hourChartData = detail ? {
    labels:   Object.keys(detail.posting_time.by_hour),
    datasets: [{
      label:           "投稿数",
      data:            Object.values(detail.posting_time.by_hour),
      backgroundColor: colors.primary,
    }],
  } : null

  const dayChartData = detail ? {
    labels:   Object.keys(detail.posting_time.by_day),
    datasets: [{
      label:           "投稿数",
      data:            Object.values(detail.posting_time.by_day),
      backgroundColor: colors.primary,
    }],
  } : null

  const p = detail?.profile
  const s = detail?.stats
  const c = detail?.comparison

  const fmtDiff = (n: number | undefined) => {
    if (n == null) return "—"
    if (n > 0) return `+${n.toLocaleString()}`
    return n.toLocaleString()
  }

  return (
    <AppLayout pageTitle="競合詳細" onBack={() => navigate("/competitors")}>
      <div className="analytics">
        {/* ヘッダー */}
        <div className="analytics__header" style={{ alignItems: "center" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, flex: 1, flexWrap: "wrap" }}>
            {p && (
              <>
                <h2 style={{ margin: 0, fontSize: 18 }}>@{p.username}</h2>
                {p.username && (
                  <InstagramLinkButton href={`https://www.instagram.com/${p.username}/`} />
                )}
              </>
            )}
          </div>
          <DateRangePicker value={range} onChange={setRange} />
        </div>

        {loading ? (
          <p style={{ color: colors.gray400 }}>読み込み中…</p>
        ) : !detail ? (
          <p style={{ color: colors.gray400 }}>データを取得できませんでした</p>
        ) : (
          <>
            {/* プロフィールカード */}
            <div className="analytics__section">
              <div className="competitor-profile-card">
                <div style={{ display: "flex", gap: 16, flex: 1 }}>
                  {p?.profile_picture_url ? (
                    <img
                      src={p.profile_picture_url}
                      alt=""
                      style={{ width: 72, height: 72, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }}
                    />
                  ) : (
                    <div style={{ width: 72, height: 72, borderRadius: "50%", background: colors.gray200, flexShrink: 0 }} />
                  )}
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 16 }}>{p?.name || `@${p?.username}`}</div>
                    {p?.name && <div style={{ color: colors.gray400, fontSize: 13 }}>@{p.username}</div>}
                    {p?.biography && (
                      <div style={{ fontSize: 13, color: colors.gray700, marginTop: 6, maxWidth: 400, whiteSpace: "pre-wrap" }}>
                        {p.biography}
                      </div>
                    )}
                    {p?.website && (
                      <>
                        <a href={p.website} target="_blank" rel="noopener noreferrer" style={{ fontSize: 13, color: colors.primary, marginTop: 4, display: "block" }}>
                          {p.website}
                        </a>
                        <div style={{ fontSize: 11, color: colors.gray400, marginTop: 2 }}>
                          ※ Instagram APIの仕様上、プロフィールに複数のリンクを設定している場合でも代表の1件のみ表示されます
                        </div>
                      </>
                    )}
                  </div>
                </div>

                {c && (
                  <div className="competitor-profile-stats">
                    {[
                      { label: "フォロワー数", comp: c.followers_count, self: c.self_followers_count },
                      { label: "期間投稿数",   comp: c.posts_count,     self: c.self_posts_count },
                      { label: "期間いいね合計", comp: c.total_likes,   self: c.self_total_likes },
                    ].map(item => (
                      <div
                        key={item.label}
                        style={{ background: "#f9fafb", borderRadius: 8, padding: "12px 16px", minWidth: 120, textAlign: "center" }}
                      >
                        <div style={{ fontSize: 11, color: colors.gray400, marginBottom: 4 }}>{item.label}</div>
                        <div style={{ fontWeight: 700, fontSize: 16, color: colors.gray900 }}>{item.comp.toLocaleString()}</div>
                        <div style={{ fontSize: 12, color: colors.gray400, marginTop: 2 }}>自: {item.self.toLocaleString()}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* StatCard 4枚 */}
            <div className="analytics__stats-grid">
              <StatCard
                label="フォロワー数"
                stat={{ value: p?.followers_count ?? null, change_rate: null }}
              />
              <StatCard
                label="フォロワー差（競合 − 自アカ）"
                stat={{ value: s?.followers_diff ?? null, change_rate: null }}
                formatter={v => (v > 0 ? `+${v.toLocaleString()}` : v.toLocaleString())}
              />
              <StatCard
                label="競合 ENG率"
                stat={{ value: s?.engagement_rate ?? null, change_rate: null }}
                unit="%"
              />
              <StatCard
                label="自アカ ENG率"
                stat={{ value: s?.self_engagement_rate ?? null, change_rate: null }}
                unit="%"
              />
            </div>

            <p style={{ fontSize: 12, color: colors.gray400, marginTop: -16, marginBottom: 24 }}>
              ※ 競合のENG率は (いいね+コメント) ÷ フォロワー数 で算出（リーチは非公開のため）
            </p>

            {/* フォロワー推移 */}
            <div className="analytics__section">
              <div className="chart-card">
                <div className="chart-card__header">
                  <span className="chart-card__title">フォロワー推移</span>
                  {followerTrendData && (
                    <div className="chart-card__actions">
                      <button className="summary-table__csv-btn" onClick={() => downloadCsv(chartDataToCsvRows(followerTrendData), `competitor_follower_trend_${todayStr()}.csv`)}>↓ CSV</button>
                      <button className="summary-table__csv-btn" onClick={() => downloadChartAsPng(followerTrendRef.current, `competitor_follower_trend_${todayStr()}.png`)}>↓ PNG</button>
                    </div>
                  )}
                </div>
                <div className="chart-card__canvas-wrap" ref={followerTrendRef}>
                  {followerTrendData
                    ? <LineChart data={followerTrendData} dateAxis />
                    : <p style={{ color: colors.gray400, fontSize: 13 }}>データ収集中です</p>
                  }
                </div>
              </div>
            </div>

            {/* 投稿時間帯・曜日 */}
            <div className="analytics__charts-grid">
              <div className="chart-card">
                <div className="chart-card__header">
                  <span className="chart-card__title">投稿時間帯</span>
                  {hourChartData && (
                    <div className="chart-card__actions">
                      <button className="summary-table__csv-btn" onClick={() => downloadCsv(chartDataToCsvRows(hourChartData), `competitor_posting_hour_${todayStr()}.csv`)}>↓ CSV</button>
                      <button className="summary-table__csv-btn" onClick={() => downloadChartAsPng(hourChartRef.current, `competitor_posting_hour_${todayStr()}.png`)}>↓ PNG</button>
                    </div>
                  )}
                </div>
                <div className="chart-card__canvas-wrap" ref={hourChartRef}>
                  {hourChartData && <BarChart data={hourChartData} />}
                </div>
                {detail.posting_time.peak_hour_label && (
                  <p style={{ fontSize: 13, color: colors.gray700, padding: "8px 16px" }}>
                    最も投稿が多い時間帯: <strong>{detail.posting_time.peak_hour_label}</strong>
                  </p>
                )}
              </div>

              <div className="chart-card">
                <div className="chart-card__header">
                  <span className="chart-card__title">投稿曜日</span>
                  {dayChartData && (
                    <div className="chart-card__actions">
                      <button className="summary-table__csv-btn" onClick={() => downloadCsv(chartDataToCsvRows(dayChartData), `competitor_posting_day_${todayStr()}.csv`)}>↓ CSV</button>
                      <button className="summary-table__csv-btn" onClick={() => downloadChartAsPng(dayChartRef.current, `competitor_posting_day_${todayStr()}.png`)}>↓ PNG</button>
                    </div>
                  )}
                </div>
                <div className="chart-card__canvas-wrap" ref={dayChartRef}>
                  {dayChartData && <BarChart data={dayChartData} />}
                </div>
                {detail.posting_time.peak_day_label && (
                  <p style={{ fontSize: 13, color: colors.gray700, padding: "8px 16px" }}>
                    最も投稿が多い曜日: <strong>{detail.posting_time.peak_day_label}</strong>
                  </p>
                )}
              </div>
            </div>

            {/* ハッシュタグ */}
            <div className="analytics__section">
              <div className="chart-card">
                <div className="chart-card__header">
                  <span className="chart-card__title">よく使うハッシュタグ TOP20</span>
                  {detail.hashtags.top.length > 0 && (
                    <button
                      className="summary-table__csv-btn"
                      onClick={() => downloadCsv(
                        [
                          ["タグ", "使用数", "平均ENG率(%)"],
                          ...detail.hashtags.top.map(t => [t.tag, t.count, t.avg_er]),
                        ],
                        `competitor_hashtags_${todayStr()}.csv`
                      )}
                    >
                      ↓ CSV
                    </button>
                  )}
                </div>
                <div className="table-scroll-wrap" style={{ padding: "0 16px 16px" }}>
                  {detail.hashtags.top.length === 0 ? (
                    <p style={{ color: colors.gray400, fontSize: 13 }}>データ収集中です</p>
                  ) : (
                    <table style={{ width: "100%", fontSize: 13, minWidth: 320 }}>
                      <thead>
                        <tr style={{ color: colors.gray400, borderBottom: "1px solid #e5e7eb" }}>
                          <th style={{ textAlign: "left", padding: "8px 4px" }}>タグ</th>
                          <th style={{ textAlign: "right", padding: "8px 4px" }}>使用数</th>
                          <th style={{ textAlign: "right", padding: "8px 4px" }}>平均ENG率</th>
                        </tr>
                      </thead>
                      <tbody>
                        {detail.hashtags.top.map(t => {
                          const isCommon    = detail.hashtags.common_with_account.includes(t.tag)
                          const isExclusive = detail.hashtags.competitor_exclusive.includes(t.tag)
                          return (
                            <tr key={t.tag} style={{ borderBottom: "1px solid #f3f4f6" }}>
                              <td style={{ padding: "6px 4px" }}>
                                {t.tag}
                                {isCommon && (
                                  <span style={{ marginLeft: 6, fontSize: 11, background: "#dcfce7", color: "#16a34a", borderRadius: 4, padding: "1px 6px" }}>共通</span>
                                )}
                                {isExclusive && (
                                  <span style={{ marginLeft: 6, fontSize: 11, background: "#fef9c3", color: "#854d0e", borderRadius: 4, padding: "1px 6px" }}>競合専用</span>
                                )}
                              </td>
                              <td style={{ textAlign: "right", padding: "6px 4px" }}>{t.count}</td>
                              <td style={{ textAlign: "right", padding: "6px 4px" }}>{t.avg_er}%</td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            </div>

            {/* 最近の投稿 */}
            <div className="analytics__section">
              <div className="chart-card">
                <div className="chart-card__header">
                  <span className="chart-card__title">最近の投稿</span>
                </div>
                <div className="competitor-posts-grid">
                  {detail.recent_posts.length === 0 ? (
                    <p style={{ color: colors.gray400, fontSize: 13, gridColumn: "span 6" }}>データ収集中です</p>
                  ) : (
                    detail.recent_posts.map((post, i) => (
                      <div key={i} style={{ position: "relative", aspectRatio: "1", borderRadius: 8, overflow: "hidden", background: colors.gray200 }}>
                        {post.thumbnail_url ? (
                          <img src={post.thumbnail_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                        ) : (
                          <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", color: colors.gray400, fontSize: 20 }}>📷</div>
                        )}
                        <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, background: "rgba(0,0,0,0.5)", color: "#fff", fontSize: 11, padding: "4px 6px", display: "flex", gap: 8 }}>
                          <span>♥ {post.likes_count.toLocaleString()}</span>
                          <span>💬 {post.comments_count.toLocaleString()}</span>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>

            {/* 人気投稿 */}
            <div className="analytics__charts-grid">
              {[
                { label: "最多いいね投稿", post: detail.top_liked_post },
                { label: "最多コメント投稿", post: detail.top_commented_post },
              ].map(({ label, post }) => (
                <div key={label} className="chart-card">
                  <div className="chart-card__header">
                    <span className="chart-card__title">{label}</span>
                  </div>
                  <div style={{ padding: "0 16px 16px" }}>
                    {!post ? (
                      <p style={{ color: colors.gray400, fontSize: 13 }}>データ収集中です</p>
                    ) : (
                      <div style={{ display: "flex", gap: 12 }}>
                        <div style={{ width: 120, height: 120, borderRadius: 8, overflow: "hidden", background: colors.gray200, flexShrink: 0 }}>
                          {post.thumbnail_url ? (
                            <img src={post.thumbnail_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                          ) : (
                            <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", color: colors.gray400, fontSize: 24 }}>📷</div>
                          )}
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 13, color: colors.gray700, marginBottom: 8, display: "flex", gap: 16 }}>
                            <span>♥ {post.likes_count.toLocaleString()}</span>
                            <span>💬 {post.comments_count.toLocaleString()}</span>
                          </div>
                          <div style={{ fontSize: 12, color: colors.gray400, marginBottom: 8 }}>
                            {new Date(post.posted_at).toLocaleDateString("ja-JP")}
                          </div>
                          {post.caption && (
                            <div style={{ fontSize: 13, color: colors.gray700, overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical" }}>
                              {post.caption}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* フォロワー変動サマリー */}
            <div className="analytics__section">
              <div className="chart-card">
                <div className="chart-card__header">
                  <span className="chart-card__title">期間サマリー比較</span>
                  {c && (
                    <button
                      className="summary-table__csv-btn"
                      onClick={() => downloadCsv(
                        [
                          ["指標", "競合", "自アカウント"],
                          ["フォロワー数", c.followers_count, c.self_followers_count],
                          ["期間フォロワー増減", fmtDiff(s?.followers_change), fmtDiff(s?.self_followers_change)],
                          ["投稿数", c.posts_count, c.self_posts_count],
                          ["いいね合計", c.total_likes, c.self_total_likes],
                          ["1投稿あたりいいね", c.avg_likes_per_post, c.self_avg_likes_per_post],
                          ["ENG率(%)", c.engagement_rate, c.self_engagement_rate],
                        ],
                        `competitor_summary_${todayStr()}.csv`
                      )}
                    >
                      ↓ CSV
                    </button>
                  )}
                </div>
                <div className="table-scroll-wrap" style={{ padding: "0 16px 16px" }}>
                  <table style={{ width: "100%", fontSize: 13, minWidth: 280 }}>
                    <thead>
                      <tr style={{ color: colors.gray400, borderBottom: "1px solid #e5e7eb" }}>
                        <th style={{ textAlign: "left", padding: "8px 4px" }}>指標</th>
                        <th style={{ textAlign: "right", padding: "8px 4px" }}>競合</th>
                        <th style={{ textAlign: "right", padding: "8px 4px" }}>自アカウント</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[
                        { label: "フォロワー数",         comp: c?.followers_count,   self: c?.self_followers_count },
                        { label: "期間フォロワー増減",    comp: fmtDiff(s?.followers_change), self: fmtDiff(s?.self_followers_change) },
                        { label: "投稿数",               comp: c?.posts_count,       self: c?.self_posts_count },
                        { label: "いいね合計",             comp: c?.total_likes,       self: c?.self_total_likes },
                        { label: "1投稿あたりいいね",      comp: c?.avg_likes_per_post, self: c?.self_avg_likes_per_post },
                        { label: "ENG率",                comp: c?.engagement_rate != null ? `${c.engagement_rate}%` : "—", self: c?.self_engagement_rate != null ? `${c.self_engagement_rate}%` : "—" },
                      ].map(row => (
                        <tr key={row.label} style={{ borderBottom: "1px solid #f3f4f6" }}>
                          <td style={{ padding: "8px 4px", color: colors.gray700 }}>{row.label}</td>
                          <td style={{ padding: "8px 4px", textAlign: "right", fontWeight: 500 }}>
                            {typeof row.comp === "number" ? row.comp.toLocaleString() : (row.comp ?? "—")}
                          </td>
                          <td style={{ padding: "8px 4px", textAlign: "right", color: colors.gray400 }}>
                            {typeof row.self === "number" ? row.self.toLocaleString() : (row.self ?? "—")}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </AppLayout>
  )
}

export default CompetitorDetailPage
